/**
 * Defines the VerticalMenu component, which renders a vertical navigation menu on the application's UI.
 */
import React, { useEffect, useState } from 'react';
import { Backdrop, Box } from '@mui/material';
import { Stack, styled } from '@mui/system';
import { VerticalMenuItem } from './VerticalMenuItem';
import { useAuth0 } from '@auth0/auth0-react';
import {
    homeMenuItemConfig,
    learnMoreMenuItemConfig,
    userActionItems
} from '../menuItemsConfig';
import { useNavigate } from 'react-router-dom';

/**
 * Styles the vertical menu container using Material-UI's styled API. The menu is designed to be fixed
 * to the right side of the viewport, with its visibility responsive to screen size.
 */
export const StyledVerticalMenu = styled(Box)(({ theme }) => ({
    width: 70,
    backgroundColor: '#ffffff',
    minHeight: '100vh',
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 900,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(12), // Adjust the padding top as needed
    paddingBottom: theme.spacing(5),
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
        width: 55 //thinner menu bar for smaller desktop screens
    },
    [theme.breakpoints.down('sm')]: {
        display: 'none'
    }
}));

const initialSelectedMenuItemID = 'HOME';

//there's a chance FS change this in future so this may need adjusting
const freshWorksIFrameParentId = 'freshworks-frame-wrapper';

/**
 * The VerticalMenu component renders a list of menu items, each represented by a VerticalMenuItem component.
 * The availability of some menu items is contingent on the user's authentication state.
 *
 * Menu items' icons change depending on whether they are currently active,
 * with the Home icon selected (filled) by default. When unselected, menu items are given an outlined icon style.
 *
 * @component
 * @returns {JSX.Element} A styled vertical menu populated with menu items
 */
export const VerticalMenu = () => {
    const { isAuthenticated } = useAuth0();
    const navigate = useNavigate();

    const [selectedItem, setSelectedItem] = useState(initialSelectedMenuItemID);
    const [widgetOpen, setWidgetOpen] = useState(false);

    const resetSelectedMenuItem = () => {
        setSelectedItem(initialSelectedMenuItemID);
    };

    /* Function to handle Home menu item click */
    const handleHomeClick = () => {
        navigate('/', { replace: true });
    };

    /* Function to handle Learn More menu item click */
    const handleLearnMoreClick = () => {
        FreshworksWidget('open');

        let iterations = 0;
        //wait for widget parent div to be in the DOM
        const checkWidgetInterval = setInterval(() => {
            iterations++;
            const widgetDiv = document.getElementById(freshWorksIFrameParentId);
            if (widgetDiv) {
                clearInterval(checkWidgetInterval);
                setWidgetOpen(true);
            }
            //if taken more than 5s to find div, give up
            if (iterations >= 25) {
                clearInterval(checkWidgetInterval);
            }
        }, 200);
    };

    /* Handle Learn More widget close */
    const closeLearnMore = () => {
        resetSelectedMenuItem();
        setWidgetOpen(false);
        FreshworksWidget('close');
    };

    /*
     * FreshService do not provide the capability to listen for a close to the widget so we
     * need to check for the existence of the iFrame's parent div every 200ms when the widget
     * is open. Once we detect a close to the widget we can reset the menu state.
     */
    useEffect(() => {
        if (widgetOpen) {
            const checkWidgetInterval = setInterval(() => {
                const widgetDiv = document.getElementById(
                    freshWorksIFrameParentId
                );

                if (!widgetDiv) {
                    clearInterval(checkWidgetInterval);
                    resetSelectedMenuItem();
                    setWidgetOpen(false);
                }
            }, 200);
        }
    }, [widgetOpen]);

    return (
        <>
            <StyledVerticalMenu data-analyticsid='sidebar'>
                <Stack spacing={{ sm: 0, md: 1 }}>
                    <VerticalMenuItem
                        itemConfig={homeMenuItemConfig}
                        onClick={handleHomeClick}
                        {...{
                            selectedItem,
                            setSelectedItem,
                            resetSelectedMenuItem
                        }}
                    />
                    {userActionItems.map((item, index) => (
                        <VerticalMenuItem
                            key={item.id}
                            itemConfig={item}
                            disabled={!isAuthenticated}
                            isDialog
                            {...{
                                selectedItem,
                                setSelectedItem,
                                resetSelectedMenuItem
                            }}
                        />
                    ))}
                </Stack>
                <VerticalMenuItem
                    itemConfig={learnMoreMenuItemConfig}
                    onClick={handleLearnMoreClick}
                    {...{
                        selectedItem,
                        setSelectedItem,
                        resetSelectedMenuItem
                    }}
                />
            </StyledVerticalMenu>
            <Backdrop
                open={widgetOpen}
                sx={{ zIndex: 1110 }}
                onClick={closeLearnMore}
            />
        </>
    );
};
